/**
 * Utility pour gérer les niveaux d'abonnement
 */

export type PlanType = 'free' | 'standard' | 'premium';

export interface Subscription {
    plan_name: string;
    status: string;
    [key: string]: any;
}

/**
 * Détermine le type de plan actuel de l'utilisateur
 */
export function getUserPlanType(subscription: Subscription | null): PlanType {
    if (!subscription || subscription.status !== 'active') {
        return 'free';
    }

    const planName = subscription.plan_name?.toLowerCase();

    if (planName === 'premium') {
        return 'premium';
    }

    if (planName === 'standard') {
        return 'standard';
    }

    return 'free';
}

/**
 * Vérifie si l'utilisateur a accès aux statistiques détaillées
 * Seul le plan Premium a accès
 */
export function hasDetailedStatsAccess(subscription: Subscription | null): boolean {
    return getUserPlanType(subscription) === 'premium';
}

/**
 * Vérifie si l'utilisateur a accès à toutes les catégories
 * Plans Standard et Premium ont accès à toutes les catégories
 */
export function hasAllCategoriesAccess(subscription: Subscription | null): boolean {
    const planType = getUserPlanType(subscription);
    return planType === 'standard' || planType === 'premium';
}

/**
 * Vérifie si l'utilisateur a accès aux résumés audio
 * Seul le plan Premium a accès
 */
export function hasAudioAccess(subscription: Subscription | null): boolean {
    return getUserPlanType(subscription) === 'premium';
}

/**
 * Vérifie si l'utilisateur a accès aux examens blancs
 * Seul le plan Premium a accès
 */
export function hasExamAccess(subscription: Subscription | null): boolean {
    return getUserPlanType(subscription) === 'premium';
}

/**
 * Vérifie si l'utilisateur a accès au format epub (Livre)
 * Accessible à tous les utilisateurs (gratuit, standard, premium)
 */
export function hasEpubAccess(_subscription: Subscription | null): boolean {
    return true;
}

/**
 * Vérifie si l'utilisateur est sur un plan payant (Standard ou Premium)
 */
export function isPaidPlan(subscription: Subscription | null): boolean {
    const planType = getUserPlanType(subscription);
    return planType === 'standard' || planType === 'premium';
}

/**
 * Retourne les limites du plan gratuit
 */
export const FREE_PLAN_LIMITS = {
    maxFlashcards: 50,
    maxQuiz: 50, // Limite par mode (50 QCM ET 50 Vrai/Faux)
    totalQuizLimit: 100, // Pour les statistiques globales (50 + 50)
    allowedCategoryIndex: 0, // Index de la première catégorie uniquement
    allowedCategoryName: 'Droits et responsabilités'
} as const;

/**
 * Vérifie si une catégorie est accessible pour le plan gratuit
 * @param categoryIndex Index de la catégorie (0-based)
 */
export function isCategoryAccessibleForFree(categoryIndex: number): boolean {
    return categoryIndex === FREE_PLAN_LIMITS.allowedCategoryIndex;
}

/**
 * Vérifie si l'utilisateur peut accéder à une catégorie
 * @param subscription Abonnement de l'utilisateur
 * @param categoryIndex Index de la catégorie (0-based)
 */
export function canAccessCategory(
    subscription: Subscription | null,
    categoryIndex: number
): boolean {
    const planType = getUserPlanType(subscription);

    // Plans payants : accès à tout
    if (planType === 'standard' || planType === 'premium') {
        return true;
    }

    // Plan gratuit : seulement la première catégorie
    return isCategoryAccessibleForFree(categoryIndex);
}

/**
 * Retourne le message de verrouillage pour une catégorie
 */
export function getCategoryLockMessage(
    subscription: Subscription | null,
    categoryIndex: number
): string | null {
    if (canAccessCategory(subscription, categoryIndex)) {
        return null;
    }

    return "Passez à un plan Standard ou Premium pour débloquer cette catégorie";
}

/**
 * Nom d'affichage du plan
 */
export function getPlanDisplayName(subscription: Subscription | null): string {
    const planType = getUserPlanType(subscription);

    switch (planType) {
        case 'premium':
            return 'Premium';
        case 'standard':
            return 'Standard';
        case 'free':
        default:
            return 'Gratuit';
    }
}
